import React, { useState, useEffect, useRef } from 'react';
import { Target, Globe, DollarSign, Lightbulb, TrendingUp, Building, BarChart3, Users, Shield, Zap } from 'lucide-react';

const StrategyPage = ({ content, language }) => {
  const [strategyVisible, setStrategyVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const strategyRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const isInViewport = (element, offset = 100) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.top <= (window.innerHeight - offset) && rect.bottom >= offset;
    };
    
    const handleScroll = () => {
      if (strategyRef.current && isInViewport(strategyRef.current)) {
        setStrategyVisible(true);
      }
      if (statsRef.current && isInViewport(statsRef.current)) {
        setStatsVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Investment thesis data
  const investmentThesis = {
    en: {
      approach: "We source and structure development deals that generate superior risk-adjusted returns while creating meaningful community impact. Our systematic approach combines traditional real estate fundamentals with innovative technology integration and cross-border market expertise.",
      dealCriteria: "We target stabilized developments generating 15-25% IRR with 3-7 year hold periods. Focus on $5M-50M projects in gateway markets (LA, Nashville, Houston) and emerging LATAM opportunities (Mexico, Argentina) with strong demographic tailwinds and infrastructure development.",
      capitalStack: "Our flexible capital stack combines institutional equity (60-70%), strategic debt financing (70-85% LTV), and alternative funding sources including impact investments, government grants, and cross-border capital partnerships to optimize returns and reduce risk.",
      uniqueAngle: "Our differentiated approach leverages three key advantages: bilingual LATAM market access, AI-enabled development workflows for enhanced efficiency, and impact-focused investment criteria that unlock ESG capital and government incentives."
    },
    es: {
      approach: "Buscamos y estructuramos acuerdos de desarrollo que generen retornos superiores ajustados al riesgo mientras creamos un impacto comunitario significativo. Nuestro enfoque sistemático combina fundamentos inmobiliarios tradicionales con integración tecnológica innovadora y experiencia en mercados transfronterizos.",
      dealCriteria: "Nos dirigimos a desarrollos estabilizados que generen 15-25% TIR con períodos de tenencia de 3-7 años. Enfoque en proyectos de $5M-50M en mercados principales (LA, Nashville, Houston) y oportunidades emergentes en LATAM (México, Argentina) con fuertes vientos demográficos y desarrollo de infraestructura.",
      capitalStack: "Nuestra estructura de capital flexible combina equity institucional (60-70%), financiamiento de deuda estratégico (70-85% LTV), y fuentes de financiamiento alternativas incluyendo inversiones de impacto, subvenciones gubernamentales y alianzas de capital transfronterizo para optimizar retornos y reducir riesgo.",
      uniqueAngle: "Nuestro enfoque diferenciado aprovecha tres ventajas clave: acceso bilingüe al mercado LATAM, flujos de trabajo de desarrollo habilitados por IA para mayor eficiencia, y criterios de inversión enfocados en impacto que desbloquean capital ESG e incentivos gubernamentales."
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div 
        ref={strategyRef}
        className="max-w-7xl mx-auto px-6 md:px-12"
      >
        <h1 className={`text-5xl font-bold mb-8 text-center transition-all duration-700 ${strategyVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          {content[language].strategy.title}
        </h1>
        <div className={`h-1 w-24 bg-white mx-auto mb-8 transition-all duration-700 delay-200 ${strategyVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
        <p className={`text-xl text-gray-300 mb-16 text-center max-w-4xl mx-auto transition-all duration-700 delay-300 ${strategyVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          {language === 'en'
            ? 'A systematic approach to cross-continental real estate development designed for institutional capital partners and strategic investors'
            : 'Un enfoque sistemático para el desarrollo inmobiliario transcontinental diseñado para socios de capital institucional e inversionistas estratégicos'}
        </p>

        {/* Investment Thesis Overview */}
        <div className={`mb-20 transition-all duration-700 delay-400 ${strategyVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <div className="grid md:grid-cols-4 gap-8">
            
            {/* Acquisition/Development Approach */}
            <div className="bg-gray-900 bg-opacity-40 rounded-xl p-8">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Acquisition Approach' : 'Enfoque de Adquisición'}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {investmentThesis[language].approach}
              </p>
            </div>

            {/* Deal Criteria */}
            <div className="bg-gray-900 bg-opacity-40 rounded-xl p-8">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-6">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Deal Criteria' : 'Criterios de Negocio'}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {investmentThesis[language].dealCriteria}
              </p>
            </div>

            {/* Capital Stack Strategy */}
            <div className="bg-gray-900 bg-opacity-40 rounded-xl p-8">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Capital Strategy' : 'Estrategia de Capital'}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {investmentThesis[language].capitalStack}
              </p>
            </div>

            {/* Unique Angle */}
            <div className="bg-gray-900 bg-opacity-40 rounded-xl p-8">
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Unique Advantage' : 'Ventaja Única'}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {investmentThesis[language].uniqueAngle}
              </p>
            </div>
          </div>
        </div>

        {/* Target Asset Classes & Markets */}
        <div className={`mb-20 transition-all duration-700 delay-500 ${strategyVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl font-bold mb-12 text-center">
            {language === 'en' ? 'Target Asset Classes & Markets' : 'Clases de Activos y Mercados Objetivo'}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-b from-blue-900 to-blue-800 rounded-xl p-8">
              <Building className="w-12 h-12 text-white mb-6" />
              <h3 className="text-2xl font-bold mb-4">
                {language === 'en' ? 'Multifamily' : 'Multifamiliar'}
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-blue-200 mb-2">
                    {language === 'en' ? 'Focus Areas:' : 'Áreas de Enfoque:'}
                  </h4>
                  <ul className="text-gray-200 space-y-1">
                    <li>• {language === 'en' ? 'Affordable housing (80-120% AMI)' : 'Vivienda asequible (80-120% AMI)'}</li>
                    <li>• {language === 'en' ? 'Mixed-use TOD projects' : 'Proyectos TOD de uso mixto'}</li>
                    <li>• {language === 'en' ? 'Fire-impacted rebuilds' : 'Reconstrucciones post-incendio'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-200 mb-2">
                    {language === 'en' ? 'Target Returns:' : 'Retornos Objetivo:'}
                  </h4>
                  <p className="text-xl font-bold text-white">18-25% IRR</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-b from-green-900 to-green-800 rounded-xl p-8">
              <TrendingUp className="w-12 h-12 text-white mb-6" />
              <h3 className="text-2xl font-bold mb-4">
                {language === 'en' ? 'Industrial' : 'Industrial'}
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-green-200 mb-2">
                    {language === 'en' ? 'Focus Areas:' : 'Áreas de Enfoque:'}
                  </h4>
                  <ul className="text-gray-200 space-y-1">
                    <li>• {language === 'en' ? 'Last-mile logistics hubs' : 'Centros logísticos de última milla'}</li>
                    <li>• {language === 'en' ? 'Cross-border trade facilities' : 'Instalaciones de comercio transfronterizo'}</li>
                    <li>• {language === 'en' ? 'Manufacturing & distribution' : 'Manufactura y distribución'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-green-200 mb-2">
                    {language === 'en' ? 'Target Returns:' : 'Retornos Objetivo:'}
                  </h4>
                  <p className="text-xl font-bold text-white">15-22% IRR</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-b from-purple-900 to-purple-800 rounded-xl p-8">
              <Globe className="w-12 h-12 text-white mb-6" />
              <h3 className="text-2xl font-bold mb-4">
                {language === 'en' ? 'LATAM Expansion' : 'Expansión LATAM'}
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-purple-200 mb-2">
                    {language === 'en' ? 'Target Markets:' : 'Mercados Objetivo:'}
                  </h4>
                  <ul className="text-gray-200 space-y-1">
                    <li>• {language === 'en' ? 'Mexico City & Guadalajara' : 'Ciudad de México y Guadalajara'}</li>
                    <li>• {language === 'en' ? 'Buenos Aires metro area' : 'Área metropolitana de Buenos Aires'}</li>
                    <li>• {language === 'en' ? 'Strategic JV partnerships' : 'Alianzas estratégicas JV'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-purple-200 mb-2">
                    {language === 'en' ? 'Target Returns:' : 'Retornos Objetivo:'}
                  </h4>
                  <p className="text-xl font-bold text-white">20-30% IRR</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Capital Stack Breakdown */}
        <div className={`mb-20 transition-all duration-700 delay-600 ${strategyVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl font-bold mb-12 text-center">
            {language === 'en' ? 'Capital Stack Structure' : 'Estructura de Capital'}
          </h2>
          
          <div className="bg-gray-900 bg-opacity-50 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold mb-6">
                  {language === 'en' ? 'Funding Sources' : 'Fuentes de Financiamiento'}
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-black bg-opacity-30 rounded-lg">
                    <span className="text-lg">{language === 'en' ? 'Institutional Equity' : 'Equity Institucional'}</span>
                    <span className="text-xl font-bold text-blue-400">60-70%</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-black bg-opacity-30 rounded-lg">
                    <span className="text-lg">{language === 'en' ? 'Senior Debt' : 'Deuda Senior'}</span>
                    <span className="text-xl font-bold text-green-400">70-85% LTV</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-black bg-opacity-30 rounded-lg">
                    <span className="text-lg">{language === 'en' ? 'Impact Capital' : 'Capital de Impacto'}</span>
                    <span className="text-xl font-bold text-purple-400">10-15%</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-black bg-opacity-30 rounded-lg">
                    <span className="text-lg">{language === 'en' ? 'Government Grants' : 'Subvenciones'}</span>
                    <span className="text-xl font-bold text-orange-400">5-10%</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold mb-6">
                  {language === 'en' ? 'Risk Mitigation' : 'Mitigación de Riesgo'}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Shield className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-2">
                        {language === 'en' ? 'Diversified Capital Sources' : 'Fuentes de Capital Diversificadas'}
                      </h4>
                      <p className="text-gray-300 text-sm">
                        {language === 'en' 
                          ? 'Multiple funding channels reduce dependency and improve execution certainty'
                          : 'Múltiples canales de financiamiento reducen dependencia y mejoran certeza de ejecución'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-2">
                        {language === 'en' ? 'Strategic Partnerships' : 'Alianzas Estratégicas'}
                      </h4>
                      <p className="text-gray-300 text-sm">
                        {language === 'en' 
                          ? 'Local market expertise and established relationships across target markets'
                          : 'Experiencia local y relaciones establecidas en mercados objetivo'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <Lightbulb className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-2">
                        {language === 'en' ? 'Technology Integration' : 'Integración Tecnológica'}
                      </h4>
                      <p className="text-gray-300 text-sm">
                        {language === 'en' 
                          ? 'AI-driven market analysis and project optimization reduce development risk'
                          : 'Análisis de mercado impulsado por IA y optimización de proyectos reduce riesgo de desarrollo'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Metrics */}
      <div 
        ref={statsRef}
        className="bg-gray-900 py-20"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className={`text-4xl font-bold mb-16 text-center transition-all duration-700 ${statsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            {language === 'en' ? 'Investment Parameters' : 'Parámetros de Inversión'}
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className={`text-center transition-all duration-700 delay-200 ${statsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              <div className="text-5xl font-bold text-blue-400 mb-2">15-25%</div>
              <p className="text-gray-300 text-lg font-medium">
                {language === 'en' ? 'Target IRR Range' : 'Rango TIR Objetivo'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {language === 'en' ? 'Risk-adjusted returns' : 'Retornos ajustados al riesgo'}
              </p>
            </div>
            
            <div className={`text-center transition-all duration-700 delay-300 ${statsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              <div className="text-5xl font-bold text-green-400 mb-2">$5M-50M</div>
              <p className="text-gray-300 text-lg font-medium">
                {language === 'en' ? 'Project Size Range' : 'Rango de Proyecto'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {language === 'en' ? 'Scalable opportunities' : 'Oportunidades escalables'}
              </p>
            </div>
            
            <div className={`text-center transition-all duration-700 delay-400 ${statsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              <div className="text-5xl font-bold text-purple-400 mb-2">3-7</div>
              <p className="text-gray-300 text-lg font-medium">
                {language === 'en' ? 'Hold Period (Years)' : 'Período Tenencia (Años)'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {language === 'en' ? 'Value-add timeline' : 'Cronología de valor agregado'}
              </p>
            </div>
            
            <div className={`text-center transition-all duration-700 delay-500 ${statsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              <div className="text-5xl font-bold text-orange-400 mb-2">70-85%</div>
              <p className="text-gray-300 text-lg font-medium">
                {language === 'en' ? 'Max LTV Ratio' : 'Ratio LTV Máximo'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {language === 'en' ? 'Conservative leverage' : 'Apalancamiento conservador'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Investor CTA */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-2xl p-12 text-center">
          <h3 className="text-4xl font-bold mb-6">
            {language === 'en' ? 'Partner With Us' : 'Asóciate Con Nosotros'}
          </h3>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            {language === 'en'
              ? 'Join institutional investors, family offices, and strategic partners in building the future of sustainable real estate development across the Americas.'
              : 'Únete a inversionistas institucionales, family offices y socios estratégicos en la construcción del futuro del desarrollo inmobiliario sostenible en las Américas.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-10 py-4 bg-white text-black text-lg font-medium rounded-lg hover:bg-gray-200 transition-colors">
              {language === 'en' ? 'Download Investment Deck' : 'Descargar Presentación'}
            </button>
            <button className="px-10 py-4 border-2 border-white text-white text-lg font-medium rounded-lg hover:bg-white hover:text-black transition-colors">
              {language === 'en' ? 'Schedule Investor Meeting' : 'Agendar Reunión'}
            </button>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-600">
            <p className="text-gray-400">
              {language === 'en' 
                ? 'Accredited investors only. Minimum investment: $250K'
                : 'Solo inversionistas acreditados. Inversión mínima: $250K'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyPage;